import React from 'react';
import { resolveRiskAlertsState, riskAlertsBadgeFor } from '../../../../ManagementProfileTab/checkStatusResolvers';
import {
    ALERT_UNITS,
    DRAWER_ROW_LABELS,
    NO_ALERTS_DRAWER_LABEL,
    RISK_ALERTS_STATE,
    RISK_ALERTS_TOOLTIPS,
} from '../../../../ManagementProfileTab/ManagementProfileTabConstants';
import type { RiskAlertsCellState, RiskAlertsState } from '../../../../ManagementProfileTab/types';
import { NoDataLabel, NotCheckedLabel } from '../../../../ManagementProfileTab/CheckStatusLabels';
import DrawerRow from './DrawerRow';
import RegulatoryCountRow from './RegulatoryCountRow';

interface RiskAlertsRowProps {
    state?: RiskAlertsState | null;
    count?: number | null;
    onClickIfPositive?: () => void;
    isPDFReport?: boolean;
}

const LABEL = DRAWER_ROW_LABELS.RISK_ALERTS;

// The count RegulatoryCountRow draws: the alerts for ALERTS, 0 for No Alerts, otherwise none (a dash).
const countFor = (resolved: RiskAlertsCellState, count?: number | null): number | null | undefined => {
    if (resolved === RISK_ALERTS_STATE.ALERTS) return count;
    return resolved === RISK_ALERTS_STATE.NO_ALERTS ? 0 : null;
};

/** The drawer's Risk Alerts row, resolved by the same rule as the Management Profile table cell. */
const RiskAlertsRow = ({ state, count, onClickIfPositive, isPDFReport }: RiskAlertsRowProps) => {
    const resolved = resolveRiskAlertsState({ riskAlertsState: state, riskIndicatorCount: count ?? undefined });
    if (resolved === RISK_ALERTS_STATE.NOT_AVAILABLE) {
        return <DrawerRow label={LABEL} valueOwnsStyling title={RISK_ALERTS_TOOLTIPS.NOT_AVAILABLE} value={<NoDataLabel />} />;
    }
    if (resolved === RISK_ALERTS_STATE.NOT_CHECKED) {
        return <DrawerRow label={LABEL} valueOwnsStyling title={RISK_ALERTS_TOOLTIPS.NOT_CHECKED} value={<NotCheckedLabel />} />;
    }
    const rowCount = countFor(resolved, count);
    return (
        <RegulatoryCountRow
            label={LABEL}
            count={rowCount}
            // Same arrow-in-circle badge as the other rows, in red, reading "N Alerts".
            badge={resolved === RISK_ALERTS_STATE.ALERTS && rowCount ? riskAlertsBadgeFor(rowCount) : null}
            unitSingular={ALERT_UNITS.SINGULAR}
            unitPlural={ALERT_UNITS.PLURAL}
            zeroLabel={NO_ALERTS_DRAWER_LABEL}
            onClickIfPositive={onClickIfPositive}
            isPDFReport={isPDFReport}
            tooltip={resolved ? RISK_ALERTS_TOOLTIPS[resolved] : undefined}
        />
    );
};

export default RiskAlertsRow;
